// Google Maps JavaScript API loader.
//
// A script tag and a promise, rather than @googlemaps/js-api-loader — the same reasoning
// CLAUDE.md uses to refuse react-router. One script, one cached promise, no dependency.
//
// THE KEY HERE IS THE BROWSER KEY, NOT THE SERVER KEY. See CLAUDE.md § Secrets:
// the server key (Geocoding, Distance Matrix) is unrestricted and must never reach the
// bundle. This one is referrer-restricted, Maps-JavaScript-only and quota-capped, and is
// public by design like the Supabase publishable key. Never swap them.

import { awaitLibraries } from './mapsReady'

export const BROWSER_MAPS_KEY = (import.meta.env.VITE_GOOGLE_MAPS_BROWSER_KEY ?? '') as string

/** False on a build with no browser key. Callers must degrade, never crash. */
export const isMapsConfigured = Boolean(BROWSER_MAPS_KEY)

let pending: Promise<void> | null = null

/**
 * Resolves once `google.maps` is genuinely usable — not merely present. Idempotent:
 * concurrent callers and remounts share one script tag and one promise. Rejects if the
 * script fails to load (bad key, referrer rejected, offline); the caller renders an
 * explanation instead of a map.
 */
export function loadGoogleMaps(): Promise<void> {
  if (!isMapsConfigured) return Promise.reject(new Error('missing_browser_maps_key'))
  if (pending) return pending

  pending = new Promise<void>((resolve, reject) => {
    const w = globalThis as { google?: { maps?: unknown }; document?: Document }
    const fail = (err: Error): void => {
      // Let a later mount retry rather than caching the failure forever.
      pending = null
      reject(err)
    }

    if (w.google?.maps) {
      awaitLibraries().then(resolve, fail)
      return
    }
    const doc = w.document
    if (!doc) {
      reject(new Error('no_document'))
      return
    }

    // Google calls this when the API and the requested libraries are ready. `onload` is
    // NOT that moment: with `loading=async` it fires as soon as the bootstrap file is
    // fetched, which can be before `google.maps` — never mind `google.maps.Map` — exists
    // at all. Reading the namespace there is what made this fail twice.
    const callback = '__spzMapsReady'
    const host = globalThis as unknown as Record<string, unknown>
    let timer = 0

    const settle = (run: () => void): void => {
      if (timer) clearTimeout(timer)
      delete host[callback]
      run()
    }

    host[callback] = () => settle(() => void awaitLibraries().then(resolve, fail))

    // A callback that never fires would leave the map spinning forever with nothing said.
    timer = setTimeout(() => settle(() => fail(new Error('maps_timeout'))), 15_000) as unknown as number

    const script = doc.createElement('script')
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(BROWSER_MAPS_KEY)}` +
      `&libraries=marker&v=weekly&loading=async&callback=${callback}`
    script.async = true
    script.onerror = () => settle(() => fail(new Error('maps_script_failed')))
    doc.head.appendChild(script)
  })

  return pending
}
